/* eslint-disable @typescript-eslint/no-misused-promises */
import { type Instance } from "@gadgetinc/mobx-quick-tree";
import { Button, Column, Row } from "@gadgetinc/widgets";
import { useStyletron } from "baseui";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import type { ChatMessageChunk } from "state-trees/src/assistant/AssistantChatMessageChunk";
import { UserMessagePadding } from "../../workspace/AssistantStyles";
import { AssistantMarkdownRenderer } from "../AssistantMarkdownRenderer";
import { useSmoothStreaming } from "../useSmoothStreaming";

export const ChatMessageChunkRenderer = observer(
  (props: { role: "user" | "system"; chunk: Instance<typeof ChatMessageChunk>; last: boolean }) => {
    const { chunk, last, role } = props;
    const smoothContent = useSmoothStreaming(chunk.content, { pause: !last });
    const isUserMessage = role === "user";
    const content = last ? smoothContent : chunk.content;

    if (content.length == 0) return null;

    return (
      <Row className="Assistant__chat-message-text-row">
        <div className="Assistant__chat-message-text-container">
          {isUserMessage ? (
            <CollapsibleMessage content={content} />
          ) : (
            <AssistantMarkdownRenderer markdown={content} fallback={<p className="Assistant__summon-message-text">{content}</p>} />
          )}
        </div>
      </Row>
    );
  }
);

export const CollapsibleMessage = ({ content, noMarkdown }: { content: string; noMarkdown?: boolean }) => {
  const [css, $theme] = useStyletron();
  const [isExpanded, setIsExpanded] = useState(false);
  const isLongMessage = content.length > userMessageShowHideThresholdLength;
  const contentToShow = isExpanded ? content : content.slice(0, 1000) + (isLongMessage ? "..." : "");

  return (
    <Column>
      <Column
        style={{
          padding: UserMessagePadding,
          paddingLeft: $theme.sizing.scale300,
          paddingRight: $theme.sizing.scale300,
          marginBottom: isLongMessage ? $theme.sizing.scale900 : 0,
        }}
      >
        {noMarkdown ? (
          contentToShow
        ) : (
          <AssistantMarkdownRenderer
            markdown={contentToShow}
            fallback={<p className="Assistant__summon-message-text">{contentToShow}</p>}
          />
        )}
      </Column>

      {isLongMessage && (
        <>
          <div
            className={css({
              position: "relative",
              marginTop: `-${fadeOverlaySize}px`,
              height: `${fadeOverlaySize}px`,
              borderBottomLeftRadius: $theme.sizing.scale400,
              borderBottomRightRadius: $theme.sizing.scale400,
              background: !isExpanded ? "linear-gradient(180deg, rgba(248, 238, 253, 0.00) 0%, #F8EEFD 69.71%)" : "none",
            })}
          >
            <div style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", marginBottom: UserMessagePadding }}>
              <Button onClick={() => setIsExpanded(!isExpanded)} kind="tertiary">
                {isExpanded ? "Show Less" : "Show More"}
              </Button>
            </div>
          </div>
        </>
      )}
    </Column>
  );
};

const userMessageShowHideThresholdLength = 1000;
const fadeOverlaySize = 128;
